import { GridPagerSettings } from '@progress/kendo-react-grid';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import CardGridWrapper from 'src/components/CardGridWrapper';
import { PictureViewCard } from 'src/components/catalog/PictureViewCard';
import ProductInformationPanel from 'src/sections/catalog_products/ProductInformationPanel';
import { Product } from 'src/types/catalog';
import { useContextSelector } from 'use-context-selector';
import { ViewCatalogProductsContext } from './ViewCatalogProductsContext';

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 25, 50, 100],
};

const ProductPictureView: FC = () => {
  const dataState = useContextSelector(ViewCatalogProductsContext, (state) => state.dataState);
  const onDataStateChange = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.onDataStateChange
  );
  const loadingProducts = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.loadingProducts
  );
  const getProductSelection = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.getProductSelection
  );
  const updateProductSelection = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.updateProductSelection
  );
  const productsTotal = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.productsTotal
  );
  const productsData = useContextSelector(ViewCatalogProductsContext, (state) => state.products);
  const search = useContextSelector(ViewCatalogProductsContext, (state) => state.search);
  const renderCard = (item: Product, index: number) => {
    const { selected, qty } = getProductSelection(item.productKey);

    return (
      <PictureViewCard
        key={item.productKey}
        product={item as any}
        selected={selected}
        onSelect={() => {
          updateProductSelection(item.productKey, {
            selected: !selected,
            qty: !selected && qty === 0 ? 1 : !selected ? qty : 0,
          });
        }}
        onQuantityChange={(newQty: number) => {
          updateProductSelection(item.productKey, {
            qty: newQty,
            selected: newQty >= 1,
          });
        }}
        quantity={qty}
      />
    );
  };

  const isProductPanelOpen = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.isProductPanelOpen
  );
  const handleCloseProductPanel = useContextSelector(
    ViewCatalogProductsContext,
    (state) => state.handleCloseProductPanel
  );

  const hasSearchValue = search && search.trim();
  const { t } = useTranslation();
  return (
    <div className="flex h-full min-h-0 flex-col">
      {hasSearchValue && (
        <div className="mb-2 shrink-0 truncate px-4 text-sm font-[600]">
          {t('Showing results for')} &quot;{search}&quot;
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4">
        <CardGridWrapper<Product>
          key={`products-${dataState.skip}-${dataState.take}`}
          dataSource={productsData ?? []}
          renderItem={renderCard}
          dataState={dataState}
          onDataStateChange={onDataStateChange}
          pageable={gridPagerSettings}
          loading={loadingProducts}
          className="min-h-0 flex-1"
          tableTextId="Items"
          stickyPager
          gap="gap-x-3 gap-y-2"
          height="100%"
          total={productsTotal}
        />
      </div>
      <ProductInformationPanel isOpen={isProductPanelOpen} onClose={handleCloseProductPanel} />
    </div>
  );
};

export default ProductPictureView;
